import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import type { SampleColumn, SampleTable } from '@/api/wsr/types';

interface DryRunResultTableProps {
  table: SampleTable;
  recordsFetched: number;
}

const STICKY_FIRST_COLUMN = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  backgroundColor: 'background.paper',
} as const;

export function DryRunResultTable({ table, recordsFetched }: DryRunResultTableProps) {
  const { t } = useCopy();

  if (table.columns.length === 0) return null;

  const title = t('tenantDetail.dryRun.table.title', 'Sample records');

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('tenantDetail.dryRun.table.count', 'Showing {{n}} of {{total}} fetched', {
          n: table.rows.length,
          total: recordsFetched,
        })}
      </Typography>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label={title}>
          <TableHead>
            <TableRow>
              {table.columns.map((column, index) => (
                <TableCell key={column.key} sx={index === 0 ? STICKY_FIRST_COLUMN : undefined}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {column.label}
                  </Typography>
                  {column.kind === 'MAPPED' ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('tenantDetail.dryRun.table.mapped', 'mapped')}
                    </Typography>
                  ) : null}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={table.columns.length} align="center">
                  {t('tenantDetail.dryRun.table.empty', 'No records in this window.')}
                </TableCell>
              </TableRow>
            ) : (
              table.rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {table.columns.map((column, columnIndex) => (
                    <TableCell
                      key={column.key}
                      sx={columnIndex === 0 ? STICKY_FIRST_COLUMN : undefined}
                    >
                      <CellValue column={column} value={row[column.key]} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function CellValue({ column, value }: { column: SampleColumn; value: string | null | undefined }) {
  const { t } = useCopy();

  if (column.kind === 'MATCH') {
    if (value === 'NO_CHANGE') {
      return (
        <Chip
          size="small"
          color="success"
          label={t('tenantDetail.dryRun.match.noChange', 'No change')}
        />
      );
    }
    if (value === 'UPDATE') {
      return (
        <Chip size="small" color="info" label={t('tenantDetail.dryRun.match.update', 'Update')} />
      );
    }
    if (value === 'CREATE') {
      return (
        <Chip
          size="small"
          color="warning"
          label={t('tenantDetail.dryRun.match.create', 'Create')}
        />
      );
    }
    return '—';
  }

  return value ?? '—';
}
